/**
 * CloakWriteConfirmModal — Maker safety net UI.
 *
 * Mounted once at AppRouter level. Subscribes to `cloakWriteGuard` and
 * resolves each pending request's Promise with the Maker's choice.
 *
 * Two visual modes:
 *  • RISKY  — red palette, no "don't ask again" checkbox, only Cancel
 *             vs "Submit as {ROLE}" — every confirmation is intentional.
 *  • BENIGN — amber palette, "Don't ask again this cloak session"
 *             checkbox visible. Once checked, subsequent benign writes
 *             pass silently until the cloak changes/exits.
 */
import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { subscribePendingConfirm, setCloakOptIn } from '@/lib/cloakWriteGuard';

export default function CloakWriteConfirmModal() {
  const [req, setReq] = useState(null);
  const [optIn, setOptIn] = useState(false);

  useEffect(() => {
    const unsub = subscribePendingConfirm((payload) => {
      setOptIn(false);
      setReq(payload);
    });
    return unsub;
  }, []);

  if (!req) return null;
  const { method, url, cloakedAs, risky, resolve } = req;

  const close = (ok) => {
    if (ok && !risky && optIn) setCloakOptIn(true);
    setReq(null);
    resolve(ok);
  };

  const palette = risky
    ? {
        ring: 'border-rose-600',
        bg: 'bg-rose-950/40',
        tag: 'bg-rose-600 text-white',
        tagLabel: 'RISKY WRITE',
        icon: <ShieldAlert size={18} className="text-rose-400" />,
        action: 'bg-rose-600 hover:bg-rose-500 text-white',
      }
    : {
        ring: 'border-amber-500',
        bg: 'bg-amber-950/40',
        tag: 'bg-amber-400 text-black',
        tagLabel: 'CLOAKED WRITE',
        icon: <AlertTriangle size={18} className="text-amber-400" />,
        action: 'bg-amber-500 hover:bg-amber-400 text-black',
      };

  // Truncate URL for display — keep query string off the headline; show it
  // smaller below.
  const [path, query] = String(url || '').split('?');

  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) close(false); }}>
      <AlertDialogContent
        data-testid="cloak-write-confirm"
        data-risky={risky ? '1' : '0'}
        className={`max-w-md border-2 ${palette.ring} ${palette.bg} text-white`}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-2 mb-2">
            {palette.icon}
            <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded ${palette.tag}`}>
              {palette.tagLabel}
            </span>
          </div>
          <AlertDialogTitle className="text-white">
            Submit this write while cloaked?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-300 text-[13px] leading-relaxed">
            You are the Maker, currently <strong className="text-white">cloaked as {String(cloakedAs || '').toUpperCase()}</strong>.
            {risky
              ? ' This endpoint changes identity, money, approvals, or platform state — it can NEVER be skipped while cloaked.'
              : ' Routine write — you can opt out of confirms for the rest of this cloak session below.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-3 rounded border border-white/10 bg-black/40 px-3 py-2 font-mono text-[11.5px] leading-relaxed">
          <div className="flex items-center gap-2">
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
              method === 'DELETE' ? 'bg-rose-600 text-white' :
              method === 'POST' ? 'bg-emerald-600 text-white' :
              'bg-sky-600 text-white'
            }`}>{method}</span>
            <span className="text-white break-all">{path}</span>
          </div>
          {query && (
            <div className="mt-1 text-zinc-400 break-all text-[10.5px]">?{query}</div>
          )}
        </div>

        {!risky && (
          <label className="flex items-center gap-2 mb-2 text-[12px] text-zinc-300 cursor-pointer select-none" data-testid="cloak-write-optin-label">
            <input
              type="checkbox"
              data-testid="cloak-write-optin-checkbox"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              className="accent-amber-400"
            />
            Don&apos;t ask again this cloak session
          </label>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="cloak-write-cancel"
            onClick={() => close(false)}
            className="bg-zinc-800 hover:bg-zinc-700 text-white border-zinc-600"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="cloak-write-confirm-submit"
            onClick={() => close(true)}
            className={palette.action}
          >
            Submit as {String(cloakedAs || 'cloaked role').toUpperCase()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
